import type { components } from "@/api/schema";

/**
 * Linked artifacts, in the browser (LINK-2).
 *
 * The server decided everything that takes judgement — which columns are
 * places or periods, which artifacts share them, how each reacts — and
 * published it as `links` beside an answer's artifacts. What is left here is
 * bookkeeping: turn a click into a selection, and a selection into what one
 * artifact should do about it. Nothing in this file matches "CA" against
 * "California"; the keys arrive already matched.
 */

export type Link = components["schemas"]["Link"];
export type LinkTarget = components["schemas"]["LinkTarget"];
export type LinkMember = components["schemas"]["LinkMember"];

/** One dimension at a time: these members of that link, at that grain. */
export interface Selection {
  link: string;
  grain: string;
  keys: string[];
  /**
   * The artifact the selection was made in, or -1 for the filter bar. That
   * artifact marks what was picked rather than filtering itself to it: a
   * county table clicked on a row does not shrink to that row.
   */
  source: number;
}

export type Reaction =
  /** Keep every mark, emphasise these (the names they are drawn under). */
  | { kind: "highlight"; marks: string[] }
  /** Show only the rows whose member is selected (a table; `rows[i]`). */
  | { kind: "filter-rows"; rows: boolean[]; shown: number }
  /** Keep every row, mark these: the table the selection was made in. */
  | { kind: "mark-rows"; rows: boolean[] }
  /** Re-shape the chart for the selection — the server's job (LINK-3). */
  | { kind: "filter-chart"; grain: string; keys: string[] };

export interface Linked {
  link: Link;
  target: LinkTarget;
}

/** Every link an artifact takes part in, by its index in the scope. */
export function linksFor(links: readonly Link[], index: number): Linked[] {
  const out: Linked[] = [];
  for (const link of links) {
    for (const target of link.targets) {
      if (target.index === index) out.push({ link, target });
    }
  }
  return out;
}

const memberIndex = new WeakMap<Link, Map<string, LinkMember>>();

/** The member with this key at this grain, from the link's own list. */
export function member(link: Link, grain: string, key: string): LinkMember | undefined {
  let index = memberIndex.get(link);
  if (!index) {
    index = new Map();
    for (const [at, members] of Object.entries(link.members)) {
      for (const entry of members) index.set(`${at}\u0000${entry.key}`, entry);
    }
    memberIndex.set(link, index);
  }
  return index.get(`${grain}\u0000${key}`);
}

/** A key restated at a coarser (or the same) grain, through its parents. */
function up(link: Link, grain: string, key: string, wanted: string): string | undefined {
  if (grain === wanted) return key;
  return member(link, grain, key)?.parents?.[wanted];
}

/** What one artifact does about the current selection, or null to stay as it is. */
export function reactionFor(
  linked: Linked,
  selection: Selection | null,
  index: number,
): Reaction | null {
  const { link, target } = linked;
  if (!selection || selection.link !== link.id) return null;
  if (selection.source === index && target.role === "column") {
    const chosen = new Set(selection.keys);
    return {
      kind: "mark-rows",
      rows: (target.rows ?? []).map((key) => key !== null && key !== undefined && chosen.has(key)),
    };
  }
  const react = target.react?.[selection.grain];
  if (!react) return null;
  const order = link.grains;
  const mine = order.indexOf(target.grain);
  const theirs = order.indexOf(selection.grain);
  const chosen = new Set(selection.keys);

  if (react === "highlight") {
    // Same grain: the selected members. Coarser target: their parents.
    const keys = new Set(
      mine === theirs
        ? selection.keys
        : selection.keys
            .map((key) => up(link, selection.grain, key, target.grain))
            .filter((key): key is string => Boolean(key)),
    );
    const marks = Object.entries(target.marks ?? {})
      .filter(([, key]) => keys.has(key))
      .map(([name]) => name);
    return { kind: "highlight", marks };
  }

  if (target.role === "column") {
    const rows = (target.rows ?? []).map((key) => {
      if (key === null || key === undefined) return false;
      const at = up(link, target.grain, key, selection.grain);
      return at !== undefined && chosen.has(at);
    });
    return { kind: "filter-rows", rows, shown: rows.filter(Boolean).length };
  }
  return { kind: "filter-chart", grain: selection.grain, keys: [...selection.keys] };
}

/**
 * A click on a mark or a row, as the next selection.
 *
 * A plain click selects that member alone, or clears it when it was already
 * the whole selection. A modified click (Cmd, Ctrl) adds or removes it, within
 * the same link and grain — mixing grains would make a selection nobody could
 * read back.
 */
export function toggle(
  current: Selection | null,
  link: Link,
  grain: string,
  key: string,
  additive: boolean,
  source: number,
): Selection | null {
  const same = current && current.link === link.id && current.grain === grain;
  if (!same) return { link: link.id, grain, keys: [key], source };
  const has = current.keys.includes(key);
  if (!additive) {
    return has && current.keys.length === 1
      ? null
      : { link: link.id, grain, keys: [key], source };
  }
  const keys = has ? current.keys.filter((entry) => entry !== key) : [...current.keys, key];
  return keys.length ? { link: link.id, grain, keys, source } : null;
}

/** The member a clicked mark stands for, trying each name the event offers. */
export function markKey(target: LinkTarget, names: readonly unknown[]): string | null {
  const marks = target.marks ?? {};
  for (const name of names) {
    if (typeof name !== "string" || !name) continue;
    const key = marks[name];
    if (key) return key;
  }
  return null;
}

/** "California", "California and Texas", "California, Texas and 2 more". */
export function describe(link: Link, selection: Selection): string {
  const labels = selection.keys.map(
    (key) => member(link, selection.grain, key)?.label ?? key,
  );
  if (labels.length <= 2) return labels.join(" and ");
  return `${labels.slice(0, 2).join(", ")} and ${labels.length - 2} more`;
}

/** The members a picker offers: the coarsest grain, where a selection usually starts. */
export function pickable(link: Link): { grain: string; members: LinkMember[] } {
  const grain = link.grains[0] ?? "";
  return { grain, members: link.members[grain] ?? [] };
}
